import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Progress } from '@/components/ui/progress';
import { useToast } from '@/hooks/use-toast';
import { supabase } from '@/integrations/supabase/client';
import { getCategoryTheme, getCategoryList } from '@/utils/categoryThemes';
import { CourseImageCard } from '@/components/course/CourseImageCard';
import { 
  RefreshCw, CheckCircle, XCircle, Image, Loader2, 
  Play, Pause, Wand2, AlertCircle
} from 'lucide-react';
import { cn } from '@/lib/utils';

interface CategoryStatus {
  category: string;
  hasImage: boolean;
  imageUrl?: string;
  isGenerating: boolean;
  error?: string;
}

const SUPABASE_URL = 'https://wxgazidevakcnkrngykk.supabase.co';

const ThumbnailGenerator: React.FC = () => {
  const { toast } = useToast();
  const [categories, setCategories] = useState<CategoryStatus[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isGeneratingAll, setIsGeneratingAll] = useState(false);
  const [progress, setProgress] = useState(0);
  
  // Load category status on mount
  useEffect(() => {
    loadCategoryStatus();
  }, []);
  
  const loadCategoryStatus = async () => {
    setIsLoading(true);
    
    try {
      // Get all unique categories from courses
      const { data: courses, error } = await supabase
        .from('courses')
        .select('category')
        .not('category', 'is', null);
      
      if (error) throw error;
      
      const uniqueCategories = [...new Set(courses.map(c => c.category))].filter(Boolean);
      
      // Check which categories have thumbnails in storage
      const categoryStatuses: CategoryStatus[] = await Promise.all(
        uniqueCategories.map(async (category) => {
          const categoryFileName = category.toLowerCase().replace(/\s+/g, '-');
          const imageUrl = `${SUPABASE_URL}/storage/v1/object/public/course-thumbnails/${categoryFileName}.png`;
          
          // Check if image exists
          try {
            const response = await fetch(imageUrl, { method: 'HEAD' });
            return {
              category,
              hasImage: response.ok,
              imageUrl: response.ok ? imageUrl : undefined,
              isGenerating: false
            };
          } catch {
            return {
              category,
              hasImage: false,
              isGenerating: false
            };
          }
        })
      );
      
      setCategories(categoryStatuses.sort((a, b) => a.category.localeCompare(b.category)));
    } catch (err) {
      console.error('Error loading categories:', err);
      toast({
        title: 'Error loading categories',
        description: err instanceof Error ? err.message : 'Unknown error',
        variant: 'destructive'
      });
    } finally {
      setIsLoading(false);
    }
  };
  
  const generateThumbnail = async (category: string) => {
    // Update UI to show generating
    setCategories(prev => prev.map(c => 
      c.category === category ? { ...c, isGenerating: true, error: undefined } : c
    ));
    
    try {
      const { data, error } = await supabase.functions.invoke('generate-course-thumbnails', {
        body: { category }
      });
      
      if (error) throw error;
      
      if (data.results?.[0]?.success) {
        setCategories(prev => prev.map(c => 
          c.category === category 
            ? { ...c, isGenerating: false, hasImage: true, imageUrl: data.results[0].url }
            : c
        ));
        toast({
          title: 'Thumbnail generated!',
          description: `Successfully created thumbnail for ${category}`
        });
      } else {
        throw new Error(data.results?.[0]?.error || 'Generation failed');
      }
    } catch (err) {
      console.error(`Error generating thumbnail for ${category}:`, err);
      setCategories(prev => prev.map(c => 
        c.category === category 
          ? { ...c, isGenerating: false, error: err instanceof Error ? err.message : 'Unknown error' }
          : c
      ));
      toast({
        title: 'Generation failed',
        description: err instanceof Error ? err.message : 'Unknown error',
        variant: 'destructive'
      });
    }
  };
  
  const generateAllMissing = async () => {
    const missingCategories = categories.filter(c => !c.hasImage);
    
    if (missingCategories.length === 0) {
      toast({
        title: 'All thumbnails exist',
        description: 'No missing thumbnails to generate'
      });
      return;
    }
    
    setIsGeneratingAll(true);
    setProgress(0);
    
    for (let i = 0; i < missingCategories.length; i++) {
      await generateThumbnail(missingCategories[i].category);
      setProgress(((i + 1) / missingCategories.length) * 100);
      
      // Add delay between generations to avoid rate limits
      if (i < missingCategories.length - 1) {
        await new Promise(resolve => setTimeout(resolve, 3000));
      }
    }
    
    setIsGeneratingAll(false);
    toast({
      title: 'Bulk generation complete',
      description: `Generated ${missingCategories.length} thumbnails`
    });
  };
  
  const existingCount = categories.filter(c => c.hasImage).length;
  const missingCount = categories.filter(c => !c.hasImage).length;
  const generatingCount = categories.filter(c => c.isGenerating).length;
  
  return (
    <div className="container mx-auto py-6 space-y-6">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
        <div>
          <h1 className="text-3xl font-bold flex items-center gap-2">
            <Wand2 className="h-8 w-8 text-primary" />
            Course Thumbnail Generator
          </h1>
          <p className="text-muted-foreground mt-1">
            Generate AI-powered thumbnails for course categories
          </p>
        </div>
        
        <div className="flex gap-3">
          <Button 
            variant="outline" 
            onClick={loadCategoryStatus}
            disabled={isLoading}
          >
            <RefreshCw className={cn('h-4 w-4 mr-2', isLoading && 'animate-spin')} />
            Refresh
          </Button>
          <Button
            onClick={generateAllMissing}
            disabled={isGeneratingAll || missingCount === 0}
          >
            {isGeneratingAll ? (
              <>
                <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                Generating...
              </>
            ) : (
              <>
                <Play className="h-4 w-4 mr-2" />
                Generate All Missing ({missingCount})
              </>
            )}
          </Button>
        </div>
      </div>
      
      {/* Stats */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <Card>
          <CardContent className="p-4 flex items-center gap-3">
            <div className="p-2 rounded-lg bg-primary/10">
              <Image className="h-5 w-5 text-primary" />
            </div>
            <div>
              <p className="text-sm text-muted-foreground">Total Categories</p>
              <p className="text-2xl font-bold">{categories.length}</p>
            </div>
          </CardContent>
        </Card>
        
        <Card>
          <CardContent className="p-4 flex items-center gap-3">
            <div className="p-2 rounded-lg bg-green-500/10">
              <CheckCircle className="h-5 w-5 text-green-500" />
            </div>
            <div>
              <p className="text-sm text-muted-foreground">With Thumbnails</p>
              <p className="text-2xl font-bold">{existingCount}</p>
            </div>
          </CardContent>
        </Card>
        
        <Card>
          <CardContent className="p-4 flex items-center gap-3">
            <div className="p-2 rounded-lg bg-yellow-500/10">
              <AlertCircle className="h-5 w-5 text-yellow-500" />
            </div>
            <div>
              <p className="text-sm text-muted-foreground">Missing</p>
              <p className="text-2xl font-bold">{missingCount}</p>
            </div>
          </CardContent>
        </Card>
        
        <Card>
          <CardContent className="p-4 flex items-center gap-3">
            <div className="p-2 rounded-lg bg-blue-500/10">
              <Loader2 className={cn('h-5 w-5 text-blue-500', generatingCount > 0 && 'animate-spin')} />
            </div>
            <div>
              <p className="text-sm text-muted-foreground">Generating</p>
              <p className="text-2xl font-bold">{generatingCount}</p>
            </div>
          </CardContent>
        </Card>
      </div>
      
      {/* Progress bar for bulk generation */}
      {isGeneratingAll && (
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center gap-4">
              <Progress value={progress} className="flex-1" />
              <span className="text-sm font-medium">{Math.round(progress)}%</span>
            </div>
          </CardContent>
        </Card>
      )}
      
      {/* Category Grid */}
      <Card>
        <CardHeader>
          <CardTitle>Category Thumbnails</CardTitle>
          <CardDescription>
            Click on a category to generate or regenerate its thumbnail
          </CardDescription>
        </CardHeader>
        <CardContent>
          {isLoading ? (
            <div className="flex items-center justify-center py-12">
              <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
            </div>
          ) : (
            <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
              {categories.map((cat) => {
                const theme = getCategoryTheme(cat.category);
                
                return (
                  <Card 
                    key={cat.category}
                    className={cn(
                      'overflow-hidden transition-all hover:shadow-lg cursor-pointer',
                      cat.isGenerating && 'ring-2 ring-primary animate-pulse'
                    )}
                    onClick={() => !cat.isGenerating && generateThumbnail(cat.category)}
                  >
                    {/* Thumbnail Preview */}
                    <div className="h-32 relative">
                      <CourseImageCard
                        category={cat.category}
                        imageUrl={cat.imageUrl}
                        className="w-full h-full"
                        showCategoryLabel={false}
                      />
                      
                      {/* Status overlay */}
                      {cat.isGenerating && (
                        <div className="absolute inset-0 bg-black/50 flex items-center justify-center">
                          <Loader2 className="h-8 w-8 text-white animate-spin" />
                        </div>
                      )}
                      
                      {/* Status badge */}
                      <div className="absolute top-2 right-2">
                        {cat.hasImage ? (
                          <Badge className="bg-green-500/90">
                            <CheckCircle className="h-3 w-3 mr-1" />
                            Generated
                          </Badge>
                        ) : (
                          <Badge variant="secondary">
                            <XCircle className="h-3 w-3 mr-1" />
                            Missing
                          </Badge>
                        )}
                      </div>
                    </div>
                    
                    {/* Category info */}
                    <CardContent className="p-3">
                      <div className="flex items-center gap-2">
                        <div className={cn('p-1.5 rounded', theme.iconBg)}>
                          <theme.icon className="h-4 w-4" style={{ color: theme.fallbackColor }} />
                        </div>
                        <span className="font-medium capitalize truncate">{cat.category}</span>
                      </div>
                      
                      {cat.error && (
                        <p className="text-xs text-destructive mt-2 truncate">
                          Error: {cat.error}
                        </p>
                      )}
                    </CardContent>
                  </Card>
                );
              })}
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default ThumbnailGenerator;
